import React, { useState, useEffect } from 'react';
import { auth, db } from '../config/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useUserProfile } from '../contexts/UserProfileContext'; // Import context

const AuthWrapper = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null); // Store user object
  const [role, setRole] = useState(null); // Store role from Firestore
  const navigate = useNavigate();
  const { setProfileImageUrl } = useUserProfile(); // Use setProfileImageUrl from context

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user); // Store the authenticated user

        // Fetch the role and profile image from Firestore
        const docRef = doc(db, 'Users', user.uid); // Assuming your collection is 'Users'
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          setRole(userData.role); // Set the role from Firestore
          
          // Fetch profile image URL and store it globally
          if (userData.profileImageUrl) {
            setProfileImageUrl(userData.profileImageUrl);
          }
        } else {
          console.log('No such document!');
        }

        setIsLoading(false);
      } else {
        navigate('/');
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, setProfileImageUrl]);

  useEffect(() => {
    let inactivityTimer;
    let lastActivityTime = 0;

    const resetInactivityTimer = () => {
      // Throttle: only reset timer at most once per second
      const now = Date.now();
      if (now - lastActivityTime < 1000) return;
      lastActivityTime = now;

      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (user) {
        inactivityTimer = setTimeout(async () => {
          await signOut(auth);
          navigate('/');
        }, 600000); // 10 minutes
      }
    };

    // Set up event listeners for user activity
    // Use passive listeners and skip mousemove (mousedown is sufficient for click detection)
    const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    // Initial timer setup
    resetInactivityTimer();

    // Cleanup function
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      activityEvents.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [user, navigate]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Pass the userId and role to the children components
  return React.cloneElement(children, { userId: user?.uid, role });
};

export default AuthWrapper;
